/**
 * Cart Item Component
 * Individual cart item display with quantity controls
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CartItem as CartItemType } from '../../contexts/CartContext';
import { formatPrice } from '../../utils';

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
  compact?: boolean;
}

const CartItem: React.FC<CartItemProps> = ({ 
  item, 
  onUpdateQuantity, 
  onRemove,
  compact = false 
}) => {
  const { product, quantity } = item;
  const subtotal = product.price * quantity;

  const handleIncrement = () => {
    if (quantity < product.stock) {
      onUpdateQuantity(product._id, quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      onUpdateQuantity(product._id, quantity - 1);
    } else {
      onRemove(product._id);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-3 py-3">
        {/* Product Image */}
        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
          {product.images && product.images.length > 0 ? (
            <img 
              src={product.images[0]} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl">
              📦
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <Link 
            to={`/products/${product._id}`}
            className="text-sm font-medium text-gray-900 hover:text-green-600 truncate block"
          >
            {product.name}
          </Link>
          <div className="flex items-center space-x-2 mt-1">
            <span className="text-xs text-gray-500">{quantity} × {formatPrice(product.price)}</span>
          </div>
        </div>

        {/* Price */}
        <div className="text-sm font-bold text-green-600">
          {formatPrice(subtotal)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start space-x-4">
        {/* Product Image */}
        <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
          {product.images && product.images.length > 0 ? (
            <img 
              src={product.images[0]} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl">
              📦
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <Link 
            to={`/products/${product._id}`}
            className="text-lg font-bold text-gray-900 hover:text-green-600"
          >
            {product.name}
          </Link>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
            {product.description}
          </p>
          
          {/* Price */}
          <div className="mt-2">
            <span className="text-sm text-gray-500">Price: </span>
            <span className="text-lg font-bold text-green-600">{formatPrice(product.price)}</span>
          </div>

          {/* Stock Info */}
          {product.stock < 10 && (
            <p className="text-xs text-orange-600 mt-1">
              Only {product.stock} left in stock!
            </p>
          )}
        </div>

        {/* Quantity Controls & Remove */}
        <div className="flex flex-col items-end space-y-3">
          {/* Quantity Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDecrement}
              className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
            >
              {quantity === 1 ? '🗑️' : '−'}
            </button>
            <input
              type="number"
              min="1"
              max={product.stock}
              value={quantity}
              onChange={(e) => {
                const newQty = parseInt(e.target.value);
                if (newQty >= 1 && newQty <= product.stock) {
                  onUpdateQuantity(product._id, newQty);
                }
              }}
              className="w-16 text-center border border-gray-300 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleIncrement}
              disabled={quantity >= product.stock}
              className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>

          {/* Subtotal */}
          <div className="text-right">
            <p className="text-xs text-gray-500">Subtotal</p>
            <p className="text-xl font-bold text-gray-900">{formatPrice(subtotal)}</p>
          </div>

          {/* Remove Button */}
          <button
            onClick={() => onRemove(product._id)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartItem;

